/**
 * The value-comparison half of the node panel's Cancel.
 *
 * Split out of `use-node-revert-point.ts` so it can be tested on its own: that
 * module imports the table store, which drags the design system (and therefore
 * a DOM) into any suite that touches it. Nothing here needs either.
 */

/**
 * Value equality for a snapshot comparison.
 *
 * `JSON.stringify` and not a reference check: array columns are rebuilt by
 * every commit, so `variables` is a NEW array with the same contents after an
 * edit that changed nothing, and a reference check would report the node dirty
 * forever. The `?? null` coalesce makes `undefined` and `null` equal, which
 * matters because a column that has never been set reads back as `undefined`
 * while a cleared one is stored as `null` — the same state to a user.
 */
export const differs = (a: unknown, b: unknown) =>
  JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);

/**
 * The columns that must be written to put the node back to `snapshot`, given a
 * reader for the node's current values.
 *
 * Pure and exported so the revert can be tested without a rendered panel. Only
 * columns that ACTUALLY differ are included: writing back every snapshotted
 * column would touch ~22 fields on every Cancel, which makes the undo entry
 * unreadable and re-saves rows nothing changed on.
 */
export const computeRevertUpdates = (
  snapshot: Record<string, unknown>,
  read: (column: string) => unknown
): Record<string, unknown> => {
  const updates: Record<string, unknown> = {};
  Object.keys(snapshot).forEach((column) => {
    if (differs(snapshot[column], read(column))) {
      updates[column] = snapshot[column];
    }
  });
  return updates;
};
